import { useRef, useState } from "react";
import { uploadEmployeeFile } from "@/lib/fileUpload";

interface ProfileCardProps {
  employeeId: string | null;
  name: string;
  email: string;
  roleLabel: string;
  avatarUrl: string | null;
  department?: string | null;
  onAvatarChange: (url: string) => void;
  onToast: (msg: string, type?: "success" | "error") => void;
}

export default function ProfileCard({
  employeeId,
  name,
  email,
  roleLabel,
  avatarUrl,
  department,
  onAvatarChange,
  onToast,
}: ProfileCardProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!employeeId) {
      onToast("無法上傳：未找到員工資料", "error");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      onToast("圖片過大，請選擇 2MB 以下的檔案", "error");
      return;
    }

    setUploading(true);
    try {
      const publicUrl = await uploadEmployeeFile(file, employeeId, "avatar_url");
      onAvatarChange(publicUrl);
      onToast("頭像更新成功", "success");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "上傳失敗";
      onToast(`頭像上傳失敗：${msg}`, "error");
    }
    setUploading(false);
  };

  return (
    <div className="bg-background-50 border border-background-200 rounded-xl p-6 flex flex-col items-center text-center">
      <div className="relative">
        <div className="w-20 h-20 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 text-2xl font-bold overflow-hidden">
          {avatarUrl ? (
            <img src={avatarUrl} alt={name} className="w-full h-full object-cover" />
          ) : (
            name.charAt(0)
          )}
        </div>
        {uploading && (
          <div className="absolute inset-0 rounded-full bg-black/30 flex items-center justify-center">
            <i className="ri-loader-4-line animate-spin text-background-50 text-lg" />
          </div>
        )}
      </div>
      <h3 className="mt-4 text-base font-bold text-foreground-900">{name}</h3>
      <p className="text-xs text-foreground-500 mt-1">
        {department || "系統管理"} · {roleLabel}
      </p>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleAvatarUpload}
      />
      <button
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        className="mt-3 px-3 py-1.5 text-xs font-medium text-foreground-600 border border-background-300 rounded-lg hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-50"
      >
        {uploading ? "處理中..." : "更換頭像"}
      </button>
      <p className="mt-2 text-xs text-foreground-400">{email}</p>
    </div>
  );
}